import NextLink from 'next/link';
import { type MutableRefObject, useCallback, useRef, useState } from 'react';

import cx from 'classnames';
import FocusTrap from 'focus-trap-react';
import { AnimatePresence, motion } from 'framer-motion';

import Close from '~/ui/assets/icons/close.svg';
import LogoWorded from '~/ui/assets/icons/logo-worded.svg';
import SearchIcon from '~/ui/assets/icons/search.svg';
import { Button, ButtonVariant } from '~/ui/components/button';
import { useBreakpoint } from '~/ui/components/grid/useBreakpoint';
import { Breakpoint } from '~/ui/styles/grid';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { Link } from '~/v1/components/link/link';
import { Route } from '~/v1/constants/route';

import { PrimaryNavigationItem } from './navigationItem';
import styles from './primary.module.scss';
import { type AnimationDuration, type NavigationItem } from '../navigation';

const ANIMATION_DURATION = 800;

interface PrimaryNavigationProps {
  className?: string;
  isLarge?: boolean;
  setActiveNavigation: (navigationItem: NavigationItem | undefined) => void;
  activeNavigation: NavigationItem | undefined;
  setPrimaryIsExpanded: (expanded: boolean) => void;
  primaryIsExpanded: boolean;
  isSticky?: boolean;
  scrollLockRef?: MutableRefObject<HTMLDivElement | null>;
  onSearchClick?: () => void;
  desktopContent?: React.ReactNode;
  showDesktopContent?: boolean;
}

export function PrimaryNavigation({
  className,
  isLarge,
  setActiveNavigation,
  activeNavigation,
  primaryIsExpanded,
  setPrimaryIsExpanded,
  scrollLockRef,
  isSticky,
  onSearchClick,
  desktopContent,
  showDesktopContent,
}: PrimaryNavigationProps) {
  const breakpoint = useBreakpoint();
  const [animationType, setAnimationType] = useState<AnimationDuration>('instant');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const togglePrimary = () => {
    timer.current && clearTimeout(timer.current);
    setAnimationType('default');
    timer.current = setTimeout(() => {
      setAnimationType('instant');
    }, ANIMATION_DURATION);
    setPrimaryIsExpanded(!primaryIsExpanded);
  };

  const isSmallOrMedium = breakpoint === Breakpoint.SM || breakpoint === Breakpoint.MD;
  const closeNavInstant = animationType === 'instant';

  const navigationClasses = cx(styles.navigation, className, {
    [styles.navigationLarge]: isLarge,
  });

  const contentTopClasses = cx(styles.contentTop, {
    [styles.contentTopScrolled]: isSticky,
    [styles.contentTopHidden]: !!activeNavigation,
    [styles.contentTopInstant]: !(!primaryIsExpanded && !!activeNavigation),
  });

  const contentBottomClasses = cx(styles.contentBottom, {
    [styles.contentBottomExpanded]: primaryIsExpanded,
    [styles.contentBottomInstant]: closeNavInstant,
  });

  const wordmarkLogoClasses = cx(styles.wordmarkLogo, {
    [styles.wordmarkLogoScrolled]: isSticky,
  });

  const handleItemsRender = useCallback((items: HTMLDivElement | null) => {
    if (!items) {
      return;
    }

    const height = items.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--primary-navigation-height', `${height / 10}rem`);
  }, []);

  return (
    <>
      <FocusTrap
        active={primaryIsExpanded && isSmallOrMedium}
        focusTrapOptions={{ initialFocus: false }}
      >
        <div className={navigationClasses} id="primary-navigation" ref={scrollLockRef}>
          <div className={contentTopClasses}>
            <div className={styles.contentTopInnerWrapper}>
              <Link href={Route.HOME} className={wordmarkLogoClasses} ariaLabel="homepage">
                <LogoWorded />
              </Link>
              <button
                className={cx(styles.menuButton, typographyStyles.link)}
                onClick={togglePrimary}
                aria-label={primaryIsExpanded ? 'Close navigation' : undefined}
              >
                {primaryIsExpanded ? <Close className={styles.closeIcon} /> : 'Menu'}
              </button>
            </div>
          </div>
          <div className={contentBottomClasses}>
            <AnimatePresence>
              {(!isSmallOrMedium || primaryIsExpanded) && (
                <motion.div
                  className={cx(styles.contentBottomInnerWrapper, {
                    [styles.showDesktopContent]: showDesktopContent,
                  })}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{
                    opacity: 0,
                    transition: {
                      duration: 0.4,
                    },
                  }}
                  transition={isSmallOrMedium ? { delay: 0.5, duration: 0.8 } : {}}
                >
                  <div className={styles.alternativeContent}>{desktopContent}</div>
                  <div className={styles.defaultContent}>
                    <div className={styles.navigationItemsWrapper} ref={handleItemsRender}>
                      <PrimaryNavigationItem
                        item="ideas"
                        activeNavigation={activeNavigation}
                        setActiveNavigation={setActiveNavigation}
                      />
                      <PrimaryNavigationItem
                        item="pages"
                        activeNavigation={activeNavigation}
                        setActiveNavigation={setActiveNavigation}
                      />
                      <PrimaryNavigationItem
                        item="search"
                        activeNavigation={activeNavigation}
                        setActiveNavigation={setActiveNavigation}
                        icon={SearchIcon}
                        onClick={onSearchClick}
                      />
                    </div>
                    <Button
                      variant={ButtonVariant.SECONDARY_TRANSPARENT}
                      className={styles.grantResourcesButton}
                      render={<NextLink href={Route.GRANT_RESOURCES} />}
                    >
                      Grant Resources
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </FocusTrap>
    </>
  );
}
